import { DataControllerRecordExportTranslations } from "../DataControllerRecord/DataControllerRecord.types";
import { DataProcessorRecordExportTranslations, DataProcessorRecordRow, DataProcessorRecordTableModel } from "./dataProcessorRecord/DataProcessorRecord.types";
import { ControllerProcess } from "../processingActivity/processingActivityOverview/ProcessingActivityOverview.types";
import { IdNamePair } from "../customLists/customListView/CustomListView.types";
import {
    DataControllerRecordColumn,
    DataControllerRecordFilterRequest,
    ProcessingActivityRecordFilterData,
    ProcessingActivityRecordType,
} from "./ProcessingActivityRecord.types";
import { groupEntityFilterId } from "./ProcessingActivityRecordFilter.types";

export interface RecordPermissions {
    canAccessRecord301: boolean;
    canAccessRecord302: boolean;
}

export function getDefaultRecordType(permissions: RecordPermissions): ProcessingActivityRecordType {
    return permissions.canAccessRecord301 ? ProcessingActivityRecordType.DataController : ProcessingActivityRecordType.DataProcessor;
}

// The view kept in session storage can name a record the user has since lost access to, so it falls back to the default.
export function resolveRecordType(requested: ProcessingActivityRecordType | undefined, permissions: RecordPermissions): ProcessingActivityRecordType {
    if (requested === undefined) {
        return getDefaultRecordType(permissions);
    }

    const canOpen = requested === ProcessingActivityRecordType.DataProcessor ? permissions.canAccessRecord302 : permissions.canAccessRecord301;
    return canOpen ? requested : getDefaultRecordType(permissions);
}

export function isRecordTypeLocked(recordType: ProcessingActivityRecordType, hasDataProcessorRecordFeature: boolean): boolean {
    return recordType === ProcessingActivityRecordType.DataProcessor && !hasDataProcessorRecordFeature;
}

// A view naming a company the user lost access to would only be answered with a 403, so it is dropped. A customer with a
// single group company has nothing to choose, which is why it is picked for them - unless the plan limit is exceeded
// or not yet known, where the record stays locked.
export function resolveGroupEntityId(
    storedGroupEntityId: string | undefined,
    groupEntities: Array<IdNamePair> | undefined,
    isPlanLimitExceeded: boolean | undefined
): string | undefined {
    if (!groupEntities || isPlanLimitExceeded !== false) {
        return undefined;
    }

    if (groupEntities.some((groupEntity) => groupEntity.id === storedGroupEntityId)) {
        return storedGroupEntityId;
    }

    return groupEntities.length === 1 ? groupEntities[0].id : undefined;
}

// The views only pick the record type, so switching keeps what the user filtered by, as the record type dropdown did,
// and starts on the first page of the other record.
export function withRecordTypeOfView(
    currentFilterData: ProcessingActivityRecordFilterData,
    viewFilterData: ProcessingActivityRecordFilterData
): ProcessingActivityRecordFilterData {
    return { ...currentFilterData, recordType: viewFilterData.recordType, pageNumber: 1 };
}

// The group company is what the record is drawn up for rather than a narrowing of it, and the .legal views name none.
// So clearing back to a view keeps the company already chosen instead of making the user pick it again, and choosing
// one does not mark such a view as changed. A view that names its own company always wins.
export function withCarriedOverGroupEntity(
    viewFilterData: ProcessingActivityRecordFilterData,
    currentFilterData: ProcessingActivityRecordFilterData | undefined
): ProcessingActivityRecordFilterData {
    if (viewFilterData.groupEntityIds || !currentFilterData?.groupEntityIds) {
        return viewFilterData;
    }

    return { ...viewFilterData, groupEntityIds: currentFilterData.groupEntityIds };
}

// The group entity is always on screen, so it is added at render rather than stored: a stored pin could be removed by
// the user and would then mark the view as changed.
export function withPinnedGroupEntity(pinnedFilterIds: Array<string> | undefined): Array<string> {
    return [groupEntityFilterId, ...withoutGroupEntity(pinnedFilterIds)];
}

export function withoutPinnedGroupEntity(pinnedFilterIds: Array<string> | undefined): Array<string> | undefined {
    const remainingIds = withoutGroupEntity(pinnedFilterIds);
    return remainingIds.length > 0 ? remainingIds : undefined;
}

function withoutGroupEntity(pinnedFilterIds: Array<string> | undefined): Array<string> {
    return (pinnedFilterIds ?? []).filter((id) => id !== groupEntityFilterId);
}

export const dataProcessorRecordUrl = "/DataProcessorRecord";

export function buildDataProcessorRecordQuery(groupEntityId: string, filterData: ProcessingActivityRecordFilterData, canAccessAllData: boolean): string {
    const parameters = new URLSearchParams({
        groupEntityId: groupEntityId,
        onlyUserSpecific: String(!canAccessAllData || filterData.showOnlyMine === true),
        processingActivityIds: (filterData.processingActivityIds?.value ?? []).join(","),
        processingAreaIds: (filterData.businessAreaIds?.value ?? []).join(","),
    });

    return `?${parameters.toString()}`;
}

export const dataControllerRecordSearchUrl = "/DataControllerRecord/search";

export const dataControllerRecordPageSize = 50;

// The header properties of the columns the search sorts on, every other column is not sortable.
const dataControllerRecordColumnById: Record<string, DataControllerRecordColumn> = {
    name: DataControllerRecordColumn.Name,
    purposes: DataControllerRecordColumn.Purposes,
    businessAreas: DataControllerRecordColumn.BusinessAreas,
    securityMeasures: DataControllerRecordColumn.SecurityMeasures,
    systems: DataControllerRecordColumn.Systems,
};

export function isSortableDataControllerRecordColumn(columnId: string): boolean {
    return columnId in dataControllerRecordColumnById;
}

export function toDataControllerRecordColumn(columnId: string): DataControllerRecordColumn {
    return dataControllerRecordColumnById[columnId] ?? DataControllerRecordColumn.Name;
}

export function toDataControllerRecordColumnId(column: DataControllerRecordColumn | undefined): string {
    return Object.keys(dataControllerRecordColumnById).find((columnId) => dataControllerRecordColumnById[columnId] === column) ?? "name";
}

export function buildDataControllerRecordRequest(
    groupEntityId: string,
    filterData: ProcessingActivityRecordFilterData,
    recordType: ProcessingActivityRecordType,
    canAccessAllData: boolean,
    searchText: string
): DataControllerRecordFilterRequest {
    return {
        groupEntityId: groupEntityId,
        showOnlyMine: !canAccessAllData || filterData.showOnlyMine === true,
        processingActivityIds: filterData.processingActivityIds,
        businessAreaIds: filterData.businessAreaIds,
        isExtended: recordType === ProcessingActivityRecordType.ExtendedDataController,
        searchText: searchText || undefined,
        pageNumber: filterData.pageNumber ?? 1,
        pageSize: dataControllerRecordPageSize,
        sortByColumn: filterData.sortByColumn,
        sortDirection: filterData.sortDirection,
    };
}

// A data controller whose name matches keeps all its processing activities; otherwise only the activities whose name,
// security measures or third country recipients match are kept, and a data controller left with none is dropped.
export function filterDataProcessorRecord(groups: Array<DataProcessorRecordTableModel>, searchText: string): Array<DataProcessorRecordTableModel> {
    const search = searchText.trim().toLowerCase();

    return groups
        .map((group) => (group.name.toLowerCase().includes(search) ? group : { ...group, rows: group.rows.filter((row) => rowMatches(row, search)) }))
        .filter((group) => group.rows.length > 0);
}

function rowMatches(row: DataProcessorRecordRow, search: string): boolean {
    return (
        row.processingActivityName.toLowerCase().includes(search) ||
        row.securityMeasures.some((securityMeasure) => securityMeasure.toLowerCase().includes(search)) ||
        row.thirdCountryTransfers.some((transfer) => transfer.toLowerCase().includes(search))
    );
}

export function getExportFileName(isDataProcessorRecord: boolean, formattedDate: string, date: Date): string {
    const prefix = isDataProcessorRecord ? "DataProcessorRecordExport-" : "DataControllerRecordExport-";
    const time = `${date.getHours().toString().padStart(2, "0")}_${date.getMinutes().toString().padStart(2, "0")}`;

    return `${prefix}${formattedDate} ${time}.xlsx`;
}

export function getDataProcessorTranslations(translateString: (key: string) => string): DataProcessorRecordExportTranslations {
    return {
        name: translateString("name"),
        address: translateString("address"),
        city: translateString("city"),
        countryCode: translateString("countryCode"),
        companyRegistrationNumber: translateString("companyRegistrationNumber"),
        processingActivityName: translateString("processingActivity"),
        securityMeasures: translateString("securityMeasures"),
        thirdCountryTransfers: translateString("recipientsThirdCountry"),
        record: translateString("dataProcessorRecord"),
        dataController: translateString("dataController"),
        dataCategories: translateString("dataCategoryHeader"),
    };
}

export function getDataControllerTranslations(translateString: (key: string) => string): DataControllerRecordExportTranslations {
    return {
        name: translateString("name"),
        address: translateString("address"),
        city: translateString("city"),
        countryCode: translateString("countryCode"),
        companyRegistrationNumber: translateString("companyRegistrationNumber"),
        purposes: translateString("purposes"),
        dataCategories: translateString("dataCategoryHeader"),
        dataSubjects: translateString("dataSubjects"),
        deletionPeriod: translateString("deletionPeriod"),
        securityMeasures: translateString("securityMeasures"),
        recipientsEuEaa: translateString("recipientsEuEaa"),
        recipientsAdequate: translateString("recipientsAdequate"),
        recipientsThirdCountry: translateString("recipientsThirdCountry"),
        record: translateString("dataControllerRecord"),
        processingActivity: translateString("processingActivity"),
        dataProtectionOfficer: translateString("dataProtectionOfficer"),
        businessAreas: translateString("businessAreas"),
        description: translateString("description"),
        disclosureBases: translateString("basisOfDisclosures"),
        legalBases: translateString("legalBasisDataProcessorRecord"),
        systems: translateString("systems"),
        transferBases: translateString("transferBasis"),
        sharingRoleController: translateString(ControllerProcess[ControllerProcess.Controller]),
        sharingRoleJointControllers: translateString(ControllerProcess[ControllerProcess.JointControllers]),
        sharingRoleProcessorW: translateString(ControllerProcess[ControllerProcess.ProcessorW]),
        sharingRoleSubprocessor: translateString(ControllerProcess[ControllerProcess.Subprocessor]),
        gDPRContact: translateString("gDPRContact"),
    };
}
